import clsx from 'clsx'
import { useMemo, useState } from 'react'
import debounce from 'debounce'
import { useQueryClient } from 'react-query'

import { Icon } from '@wartek-id/icon-v2'
import { InputGroup, Input, InputLeftAddon } from '@wartek-id/input'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast-v2'

import { queryKeys } from 'api/kinerja/tim-penilai-ps/config'
import { Header } from 'app/KinerjaShared/components/Header'
import { TablePenetapan } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/TablePenetapan'
import { Filters } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/Filters'
import useBasePath from 'app/KinerjaTimPenilaiPSPage/hooks/useBasePath'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import {
  useAPIGetPenetapanPegawai,
  useAPISavePenetapanPegawai,
  useAPISendPenetapanPegawaiToPPK,
} from 'api/kinerja/tim-penilai-ps/penetapan'
import { EmptyStateImage } from 'components/EmptyState'
import {
  PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS,
  PENETAPAN_PEGAWAI_FILTER_LABELS,
} from 'app/KinerjaTimPenilaiPSPage/constants/penetapan'
import { OnboardingPenetapan } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/Onboarding'
import {
  FOOTER_ID,
  STEP_IDS,
} from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/Onboarding/steps'
import { usePenetapanTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePenetapanTrackEvent'

import type { ActionBulkSendToPPKOptions } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/TablePenetapan/ActionBulkSendToPPK'
import type {
  PenetapanPegawaiFilters,
  RequestSavePenetapanPegawai,
} from 'types/kinerja-tim-penilai-ps'

function MainPenetapanPegawaiList() {
  const toast = useToast()
  const { basePath } = useBasePath()
  const queryClient = useQueryClient()
  const { period } = usePeriodContext()
  const { trackEvent } = usePenetapanTrackEvent()

  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<PenetapanPegawaiFilters>({
    predikatKinerja: '',
    status: '',
    nama: '',
    limit: 10,
  })

  // Get Penetapan Pegawai List
  const {
    data: responseData,
    isFetching,
    isFetched,
  } = useAPIGetPenetapanPegawai({
    params: {
      offset: (page - 1) * filters.limit,
      ...filters,
    },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      onSuccess: () => {
        trackEvent('tabel pegawai', 'view')
      },
    },
  })

  const data = useMemo(() => responseData?.data, [responseData?.data])
  const meta = useMemo(() => responseData?.meta, [responseData?.meta])
  const showPagination = meta?.total > filters.limit
  const hasFiltersAndEmpty =
    Object.values(filters).some((filter) => Boolean(filter)) &&
    meta?.total === 0 &&
    isFetched

  const getEmptyMessage = () => {
    if (filters?.status && filters?.predikatKinerja) {
      return `Status "${
        PENETAPAN_PEGAWAI_FILTER_LABELS[filters?.status]
      }" & Predikat Kinerja "${
        PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS[filters?.predikatKinerja]
      }" tidak ditemukan. Coba lagi.`
    }

    if (filters?.predikatKinerja) {
      return `Predikat Kinerja "${
        PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS[filters?.predikatKinerja]
      }" tidak ditemukan. Coba lagi.`
    }

    if (filters?.status) {
      return `Status "${
        PENETAPAN_PEGAWAI_FILTER_LABELS[filters?.status]
      }" tidak ditemukan. Coba lagi.`
    }

    if (filters?.nama) {
      return `Nama “${filters?.nama}” tidak ditemukan. Cek ejaan dan coba lagi.`
    }

    return ''
  }

  const resetQueries = () => {
    // Invalidate the get List & Detail pegawai queries
    queryClient.invalidateQueries([
      queryKeys.API_GET_PENETAPAN_PEGAWAI,
      {
        period: period.id,
      },
    ])
    queryClient.invalidateQueries([
      queryKeys.API_GET_PENETAPAN_PEGAWAI_DETAIL,
      {
        period: period.id,
      },
    ])
    queryClient.invalidateQueries([
      queryKeys.API_GET_PENETAPAN_PEGAWAI_COUNT_STATUS,
      {
        period: period.id,
      },
    ])
    queryClient.invalidateQueries([
      queryKeys.API_GET_PENETAPAN_PEGAWAI_COUNT_PREDICATE,
      {
        period: period.id,
      },
    ])
  }

  // Save Penetapan
  const {
    mutate: savePenetapan,
    isLoading: isLoadingSavePenetapan,
    isSuccess: isSuccessSavePenetapan,
  } = useAPISavePenetapanPegawai({})

  const handleSubmitPenetapan = (payload: RequestSavePenetapanPegawai) => {
    savePenetapan(payload, {
      onSuccess: () => {
        // Show toast message
        toast({ message: 'Berhasil simpan Rating!', type: 'success' })

        trackEvent('tabel pegawai', 'save multiple predikat kinerja')

        resetQueries()
      },
      onError: () => {
        // Show toast message
        toast({
          message: 'Gagal simpan Rating!. Coba lagi.',
        })
      },
    })
  }

  // Send Penetapan to PPK
  const { mutate: sendToPPK, isLoading: isLoadingSendToPPK } =
    useAPISendPenetapanPegawaiToPPK({})
  const handleSendToPPK = (
    payload: Pick<RequestSavePenetapanPegawai, 'pengawasSekolahIds'>,
    options: ActionBulkSendToPPKOptions = {}
  ) => {
    const { onSuccess, onError } = options

    sendToPPK(payload, {
      onSuccess: () => {
        onSuccess?.()

        trackEvent('tabel pegawai', 'send predikat kinerja')
      },
      onError: () => {
        onError?.()

        // Show toast message
        toast({
          message: 'Gagal kirim Penetapan ke PPK. Coba lagi.',
        })
      },
    })
  }

  return (
    <div className="min-h-full px-8 pt-12 pb-20 bg-gray-10">
      <div className="pb-12 mx-auto max-w-screen-2xl">
        <Header
          title="Rekomendasi Predikat Kinerja"
          className="mb-12"
          rightNode={<OnboardingPenetapan.OnboardingInfoButton />}
        />

        <main>
          <div className="grid grid-cols-2 mb-4 space-x-4">
            <div className="flex items-center space-x-4">
              <Text variant="heading-md">
                Daftar Pengawas Sekolah{' '}
                {(data?.pengawasSekolah ?? []).length > 0 &&
                  `(${data.pengawasSekolah.length})`}
              </Text>
              <div className="flex-none">
                <InputGroup>
                  <InputLeftAddon
                    className="pl-4"
                    style={{ maxHeight: 'inherit' }}
                  >
                    <Icon>search</Icon>
                  </InputLeftAddon>
                  <Input
                    placeholder="Cari"
                    className="!max-w-52 !p-3 !pl-10 !text-sm"
                    onChange={debounce((e) => {
                      setFilters({
                        ...filters,
                        nama: e.target.value,
                      })
                    }, 200)}
                  />
                </InputGroup>
              </div>
            </div>
            <div>
              <Filters
                data={data}
                filters={filters}
                onSetFilters={setFilters}
              />
            </div>
          </div>
          {hasFiltersAndEmpty ? (
            <div className="py-20 text-center bg-white border rounded-md border-subdued">
              <EmptyStateImage
                data-testid="error-boundary-image"
                imageSrc="/images/errors/img-empty-search-result.png"
              />
              <Text>{getEmptyMessage()}</Text>
            </div>
          ) : (
            <TablePenetapan.Provider
              data={data}
              meta={meta}
              isFetching={isFetching}
            >
              <div className="space-y-4">
                <TablePenetapan.Table
                  tableId={STEP_IDS.TABLE_ID}
                  nextPageResolver={(pengawasSekolahId) =>
                    `${basePath}/penilai/penetapan/${pengawasSekolahId}`
                  }
                />
                {showPagination && (
                  <TablePenetapan.Pagination
                    totalItems={meta?.total}
                    pageSize={filters.limit}
                    currentPage={page}
                    onPageChange={setPage}
                  />
                )}
              </div>

              <footer
                className={clsx(
                  'fixed bottom-0 left-0',
                  'w-full',
                  'bg-white shadow-[0_-1px_3px_0_rgba(0,0,0,0.1),0_-1px_2px_-1px_rgba(0,0,0,0.1)]'
                )}
              >
                <TablePenetapan.Toolbar
                  isFetching={isFetching}
                  footerId={FOOTER_ID}
                >
                  <div className="flex gap-6">
                    <TablePenetapan.ActionBulkPenetapan
                      isSubmitting={isLoadingSavePenetapan}
                      isSubmitSuccess={isSuccessSavePenetapan}
                      onSubmit={handleSubmitPenetapan}
                    />
                    <TablePenetapan.ActionBulkSendToPPK
                      isSubmitting={isLoadingSendToPPK}
                      onSubmit={handleSendToPPK}
                      onCloseSuccessDialog={resetQueries}
                    />
                  </div>
                </TablePenetapan.Toolbar>
              </footer>

              {isFetched && meta?.total > 0 && (
                <OnboardingPenetapan.Onboarding />
              )}
            </TablePenetapan.Provider>
          )}
        </main>
      </div>
    </div>
  )
}

export default function PenetapanPegawaiList() {
  return (
    <OnboardingPenetapan.Provider>
      <MainPenetapanPegawaiList />
    </OnboardingPenetapan.Provider>
  )
}
